import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Input,
  Modal,
  Popover,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { HistoryOutlined, LockOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { createCohort, listCohorts, listCohortsByIds } from '../../actions/cohortActions';
import { COHORT_CREATE_RESET } from '../../constants/cohortConstants';
import {
  buildCohortCreatePayload,
  generateSessionName,
} from '../crud/utils/cohortNameUtils';
import useUploadSessionNameConfig from '../../hooks/useUploadSessionNameConfig';

const { Text } = Typography;

const PRIMARY_SEARCH_PROP = 'cohortName';
const SECONDARY_SEARCH_PROP = 'description';

const findCohortByName = (cohorts, name) =>
  cohorts.find((c) => c.cohortName === name) ?? null;

const UploadSessionManager = ({ value, onChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  const { cohorts = [], loading: loadingList } = useSelector(
    (state) => state.cohortList || {}
  );
  const { cohorts: cohortsByIds = [] } = useSelector(
    (state) => state.cohortListByIds || {}
  );
  const {
    loading: creating,
    success: createSuccess,
    error: createError,
    cohort: createdCohort,
  } = useSelector((state) => state.cohortCreate || {});

  const { config: sessionNameConfig } = useUploadSessionNameConfig();

  const currentSessionName = useMemo(
    () => generateSessionName(userInfo),
    [userInfo, sessionNameConfig]
  );

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isPastOverride, setIsPastOverride] = useState(false);
  const [activeCohort, setActiveCohort] = useState(null);
  const [resolvedCurrentMonthCohort, setResolvedCurrentMonthCohort] = useState(null);
  const [resolvingCurrent, setResolvingCurrent] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [searchProp, setSearchProp] = useState(PRIMARY_SEARCH_PROP);
  const [entityListFilter, setEntityListFilter] = useState({
    query: '',
    page: 1,
    limit: 10,
    filter: PRIMARY_SEARCH_PROP,
  });

  const resolveCurrentMonthCohort = useCallback(() => {
    dispatch(
      listCohorts({
        filter: PRIMARY_SEARCH_PROP,
        query: currentSessionName,
        page: 1,
        limit: 10,
      })
    );
  }, [dispatch, currentSessionName]);

  useEffect(() => {
    resolveCurrentMonthCohort();
    return () => dispatch({ type: COHORT_CREATE_RESET });
  }, [dispatch, resolveCurrentMonthCohort]);

  useEffect(() => {
    const match = findCohortByName(cohorts, currentSessionName);
    if (match) setResolvedCurrentMonthCohort(match);
    if (!loadingList) setResolvingCurrent(false);
  }, [cohorts, currentSessionName, loadingList]);

  useEffect(() => {
    if (isPastOverride) return;
    if (resolvedCurrentMonthCohort && resolvedCurrentMonthCohort.id !== value) {
      setActiveCohort(resolvedCurrentMonthCohort);
      onChange?.(resolvedCurrentMonthCohort.id);
    }
  }, [resolvedCurrentMonthCohort, isPastOverride, value]);

  useEffect(() => {
    if (!value) return;
    dispatch(listCohortsByIds([value]));
  }, [value, dispatch]);

  useEffect(() => {
    if (!value) return;
    const matched = cohortsByIds.find((c) => c.id === value);
    if (!matched) return;
    setActiveCohort(matched);
    setIsPastOverride(matched.cohortName !== currentSessionName);
  }, [value, cohortsByIds, currentSessionName]);

  useEffect(() => {
    if (!createSuccess || !createdCohort) return;
    setResolvedCurrentMonthCohort(createdCohort);
    setActiveCohort(createdCohort);
    setIsPastOverride(false);
    onChange?.(createdCohort.id);
    dispatch({ type: COHORT_CREATE_RESET });
    resolveCurrentMonthCohort();
    message.success(t('Upload session created'));
  }, [createSuccess, createdCohort, dispatch, onChange, resolveCurrentMonthCohort, t]);

  useEffect(() => {
    if (!createError) return;
    const errorText = Array.isArray(createError) ? createError.join(', ') : createError;
    message.error(errorText || t('Failed to create upload session'));
  }, [createError, t]);

  const debouncedSearch = useCallback(
    debounce((searchValue, filter) => {
      const newFilter = {
        ...entityListFilter,
        query: searchValue || '',
        filter,
        page: 1,
      };
      setEntityListFilter(newFilter);
      if (popoverOpen) {
        dispatch(listCohorts(newFilter));
      }
    }, 300),
    [entityListFilter, popoverOpen, dispatch]
  );

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchText(searchValue);
    debouncedSearch(searchValue, searchProp);
  };

  const handleSearchPropChange = (prop) => {
    setSearchProp(prop);
    const newFilter = {
      ...entityListFilter,
      filter: prop,
      query: searchText,
      page: 1,
    };
    setEntityListFilter(newFilter);
    if (popoverOpen) {
      dispatch(listCohorts(newFilter));
    }
  };

  const handlePopoverOpenChange = (visible) => {
    setPopoverOpen(visible);
    if (visible) {
      dispatch(listCohorts(entityListFilter));
    }
  };

  const handleCreate = () => {
    dispatch({ type: COHORT_CREATE_RESET });
    dispatch(createCohort(buildCohortCreatePayload(currentSessionName)));
  };

  const handleSelectOther = (cohort) => {
    Modal.confirm({
      title: t('Switch upload session?'),
      content: t('Files will be uploaded to {{name}}. Continue?', {
        name: cohort.cohortName,
      }),
      okText: t('Switch'),
      cancelText: t('Cancel'),
      onOk: () => {
        setIsPastOverride(cohort.cohortName !== currentSessionName);
        setActiveCohort(cohort);
        onChange?.(cohort.id);
        setPopoverOpen(false);
      },
    });
  };

  const handleUseCurrent = () => {
    if (!resolvedCurrentMonthCohort) return;
    setIsPastOverride(false);
    setActiveCohort(resolvedCurrentMonthCohort);
    onChange?.(resolvedCurrentMonthCohort.id);
    setPopoverOpen(false);
  };

  const searchOptions = useMemo(
    () => [
      { value: PRIMARY_SEARCH_PROP, label: t(PRIMARY_SEARCH_PROP) },
      { value: SECONDARY_SEARCH_PROP, label: t(SECONDARY_SEARCH_PROP) },
    ],
    [t]
  );

  const otherSessions = useMemo(
    () => cohorts.filter((c) => c.cohortName !== currentSessionName),
    [cohorts, currentSessionName]
  );

  const columns = useMemo(
    () => [
      {
        key: 'content',
        render: (_, record) => (
          <span>
            {record[PRIMARY_SEARCH_PROP]}
            {record[SECONDARY_SEARCH_PROP] && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                | {record[SECONDARY_SEARCH_PROP]}
              </Text>
            )}
          </span>
        ),
      },
    ],
    []
  );

  const isLockedToCurrent =
    !isPastOverride && activeCohort?.cohortName === currentSessionName;
  const showCreate = !resolvedCurrentMonthCohort && !isPastOverride;

  const popoverContent = (
    <div style={{ width: 300 }}>
      {isPastOverride && resolvedCurrentMonthCohort && (
        <Button
          type="link"
          size="small"
          icon={<LockOutlined />}
          onClick={handleUseCurrent}
          style={{ padding: '0 0 8px', height: 'auto' }}
        >
          {currentSessionName}
        </Button>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder={t('Search')}
          value={searchText}
          onChange={handleSearch}
          style={{ flex: 1 }}
          allowClear
        />
        <Select
          placeholder={t('Search by')}
          style={{ width: 100 }}
          size="small"
          value={searchProp}
          onChange={handleSearchPropChange}
          options={searchOptions}
        />
      </div>
      <Table
        size="small"
        dataSource={otherSessions}
        columns={columns}
        pagination={false}
        showHeader={false}
        rowKey="id"
        loading={loadingList}
        locale={{ emptyText: t('No other upload sessions') }}
        onRow={(record) => ({
          onClick: () => handleSelectOther(record),
          style: { cursor: 'pointer' },
        })}
        style={{ maxHeight: 300, overflow: 'auto' }}
      />
    </div>
  );

  if (resolvingCurrent && !activeCohort) {
    return <Spin size="small" />;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {!showCreate && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {isLockedToCurrent && (
            <Text type="secondary" style={{ flexShrink: 0, lineHeight: 1 }}>
              <LockOutlined />
            </Text>
          )}
          <Text strong={isLockedToCurrent} ellipsis style={{ flex: 1, minWidth: 0 }}>
            {activeCohort?.cohortName || currentSessionName}
          </Text>
          {isPastOverride && (
            <Tag style={{ margin: 0, fontSize: 11, flexShrink: 0 }}>{t('Past session')}</Tag>
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          flex: showCreate ? 1 : 'none',
          minWidth: 0,
        }}
      >
        {showCreate && (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            loading={creating}
            onClick={handleCreate}
            block
            style={{ flex: 1, minWidth: 0 }}
          >
            {t('Create {{name}}', { name: currentSessionName })}
          </Button>
        )}
        <Popover
          content={popoverContent}
          title={t('Other upload sessions')}
          trigger="click"
          open={popoverOpen}
          onOpenChange={handlePopoverOpenChange}
          placement="bottomRight"
          getPopupContainer={(trigger) => trigger.closest('.ant-modal-content') || document.body}
        >
          <Button
            type="text"
            size="small"
            icon={<HistoryOutlined />}
            aria-label={t('Other upload sessions')}
            loading={popoverOpen && loadingList}
            style={{ flexShrink: 0 }}
          />
        </Popover>
      </div>
    </div>
  );
};

export default UploadSessionManager;
